import React, { useMemo } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useGetShareActionConfig } from './getAction';
import type { ShareActionComponentProps } from './types';

export type ShareAction<CH extends ShareChannel = ShareChannel> = {
	channel: CH;
	/** The canonical long URL. Use `shortUrl ?? url` when you want the shortest available link. */
	url: string;
	/** The short URL when available; `undefined` if shortening was not performed or failed. */
	shortUrl?: string;
	// FIXME can we make this type require a specific onClick prop?
	// This currently does not prevent passing components that do not accept onClick
	ShareAction: (props: ShareActionComponentProps) => React.JSX.Element;
};

type Options<CH extends ShareChannel = ShareChannel> = {
	config: ShareConfig;
	trackingEventName: string;
	getUrl: (channel: CH) => string;
	getShortUrl: (channel: CH) => string;
	getShareId?: (channel: CH) => string | undefined;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

export function useShareActionsCore<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	options: Options<CH>,
): ReadonlyArray<ShareAction<CH>> {
	const getShareActionConfig = useGetShareActionConfig(options);
	const { getUrl, getShortUrl } = options;
	return useMemo(
		() =>
			channels.map((channel) => {
				const url = getUrl(channel);
				const shortUrl = getShortUrl(channel);
				const shareActionConfig = getShareActionConfig(channel);

				const ShareAction = (() => {
					if (shareActionConfig.type === 'function') {
						const shareActionFn = shareActionConfig.action;
						const Component = ({ children, onBeforeAction, onAfterAction }: ShareActionComponentProps) => {
							const onClick = useMemo(() => {
								return () => {
									// eslint-disable-next-line max-nested-callbacks
									void (async () => {
										await onBeforeAction?.();
										const success = await shareActionFn();
										await onAfterAction?.(success);
									})();
								};
							}, [onBeforeAction, onAfterAction]);

							return React.cloneElement(children, {
								...children.props,
								onClick,
							});
						};
						return Component;
					}

					return shareActionConfig.Component;
				})();

				return {
					channel,
					url,
					shortUrl: url === shortUrl ? undefined : shortUrl,
					ShareAction,
				};
			}),
		[channels, getShareActionConfig, getUrl, getShortUrl],
	);
}
